/**
 * 日陰考慮の経路探索（被曝マップ「代替経路の被曝差」の発展形）。
 *
 * 地形タイルの5mグリッド上でダイクストラ法を回し、3パターンを返す：
 *   1. 最短ルート（時間最小）
 *   2. 日陰重視ルート（所要時間 ≤ 最短 × maxTimeRatio、既定1.5倍）
 *   3. 日陰重視ルート（所要時間 ≤ 最短 + maxExtraMinutes、既定10分）
 *
 * 時間制約つき被曝最小化は、辺コスト = 時間 + λ×被曝 の λ を二分探索して
 * 「制約内で最も日陰寄り」の経路を選ぶ（λ=0が最短、λ→∞が被曝最小）。
 * 被曝レートはエンジンと同じ式（§4.2）で各セルについて評価する。
 */
import { DEFAULT_ROUTE_PREFS, F_POSE, UVI_TO_WM2, WALK_SPEED_M_PER_MIN } from "./constants.js";
import { sector, TerrainTile } from "./terrain.js";
import { fdUv } from "./wx.js";
import type { LatLon, RoutePrefs, WxMinute } from "./types.js";

export interface RouteResult {
  /** 経路（セル中心の緯度経度列） */
  path: LatLon[];
  /** 各経路点の日向フラグ（path と同じ長さ。false=日陰。地図の色分け用） */
  sunnyFlags: boolean[];
  /** 所要時間 [min] */
  timeMin: number;
  /** 経路上の被曝量 [SED]（未塗布・指定時刻の気象/太陽で概算） */
  sed: number;
  /** 日陰率（移動時間に占める太陽不可視の割合 0..1） */
  shadeShare: number;
}

export interface RoutePlan {
  shortest: RouteResult;
  /** 日陰重視①：時間 ≤ 最短×maxTimeRatio */
  shadeWithinRatio: RouteResult;
  /** 日陰重視②：時間 ≤ 最短＋maxExtraMinutes */
  shadeWithinExtra: RouteResult;
  prefs: RoutePrefs;
}

export interface RouteQuery {
  tile: TerrainTile;
  start: LatLon;
  goal: LatLon;
  sun: { elevDeg: number; azDeg: number };
  wx: Pick<WxMinute, "uvi" | "fdBb">;
  prefs?: Partial<RoutePrefs>;
}

/** セルごとの被曝レート [SED/min] と日向フラグを前計算 */
function buildExposure(
  tile: TerrainTile,
  sun: RouteQuery["sun"],
  wx: RouteQuery["wx"],
): { sedPerMin: Float64Array; sunny: Uint8Array; walkable: Uint8Array } {
  const n = tile.cellsX * tile.cellsY;
  const sedPerMin = new Float64Array(n);
  const sunny = new Uint8Array(n);
  const walkable = new Uint8Array(n);
  const sec = sector(sun.azDeg);
  const fd = fdUv(wx.fdBb);
  const eOpen = UVI_TO_WM2 * wx.uvi;
  for (let y = 0; y < tile.cellsY; y++) {
    for (let x = 0; x < tile.cellsX; x++) {
      const i = y * tile.cellsX + x;
      const cell = tile.cellAt(x, y);
      walkable[i] = cell.walkable === false ? 0 : 1;
      const visible = sun.elevDeg > (cell.horizonDeg[sec] ?? 0) ? 1 : 0;
      sunny[i] = visible;
      const eSkin = (eOpen * (1 - fd) * visible + eOpen * fd * cell.svf) * F_POSE;
      sedPerMin[i] = (eSkin * 60) / 100; // W/m² → SED/min
    }
  }
  return { sedPerMin, sunny, walkable };
}

/** スナップ探索の最大半径 [セル]（5mセルで150m。駅舎など大きな建物の中心から街路まで届く距離） */
const SNAP_RADIUS_CELLS = 30;

/**
 * 歩行可能セルの連結成分（8近傍）をラベル付けする。
 * 建物のあいだには1〜数セルの孤立した隙間ができるため、単純な最近傍スナップだと
 * そこへ吸着して「経路が見つからない」になる。街路網＝最大成分を選ぶために使う。
 */
function labelComponents(
  tile: TerrainTile,
  walkable: Uint8Array,
): { label: Int32Array; largest: number } {
  const nx = tile.cellsX;
  const n = nx * tile.cellsY;
  const label = new Int32Array(n).fill(-1);
  const stack = new Int32Array(n);
  let largest = -1;
  let largestSize = 0;
  let next = 0;

  for (let seed = 0; seed < n; seed++) {
    if (!walkable[seed] || label[seed] !== -1) continue;
    const id = next++;
    let top = 0;
    let size = 0;
    stack[top++] = seed;
    label[seed] = id;
    while (top > 0) {
      const u = stack[--top]!;
      size++;
      const ux = u % nx;
      const uy = (u / nx) | 0;
      for (const [dx, dy] of DIRS) {
        const vx = ux + dx;
        const vy = uy + dy;
        if (vx < 0 || vy < 0 || vx >= nx || vy >= tile.cellsY) continue;
        const v = vy * nx + vx;
        if (!walkable[v] || label[v] !== -1) continue;
        label[v] = id;
        stack[top++] = v;
      }
    }
    if (size > largestSize) {
      largestSize = size;
      largest = id;
    }
  }
  return { label, largest };
}

/**
 * 指定した連結成分に属する最寄りセルへスナップする。
 * 駅や施設の座標は建物の内側を指すことが多いため、街路網（最大成分）まで寄せる。
 */
function snapToComponent(
  tile: TerrainTile,
  label: Int32Array,
  wantComp: number,
  pos: LatLon,
): { x: number; y: number } | null {
  const idx = tile.cellIndexOf(pos);
  if (!idx) return null;
  for (let r = 0; r <= SNAP_RADIUS_CELLS; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = idx.x + dx;
        const y = idx.y + dy;
        if (x < 0 || y < 0 || x >= tile.cellsX || y >= tile.cellsY) continue;
        if (label[y * tile.cellsX + x] === wantComp) return { x, y };
      }
    }
  }
  return null;
}

/** 二分ヒープ（最小） */
class MinHeap {
  private cost: number[] = [];
  private node: number[] = [];
  get size(): number {
    return this.cost.length;
  }
  push(c: number, n: number): void {
    const cost = this.cost;
    const node = this.node;
    cost.push(c);
    node.push(n);
    let i = cost.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (cost[p]! <= cost[i]!) break;
      [cost[p], cost[i]] = [cost[i]!, cost[p]!];
      [node[p], node[i]] = [node[i]!, node[p]!];
      i = p;
    }
  }
  pop(): [number, number] {
    const cost = this.cost;
    const node = this.node;
    const top: [number, number] = [cost[0]!, node[0]!];
    const lc = cost.pop()!;
    const ln = node.pop()!;
    if (cost.length > 0) {
      cost[0] = lc;
      node[0] = ln;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < cost.length && cost[l]! < cost[m]!) m = l;
        if (r < cost.length && cost[r]! < cost[m]!) m = r;
        if (m === i) break;
        [cost[m], cost[i]] = [cost[i]!, cost[m]!];
        [node[m], node[i]] = [node[i]!, node[m]!];
        i = m;
      }
    }
    return top;
  }
}

const DIRS: [number, number, number][] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

interface Grid {
  tile: TerrainTile;
  sedPerMin: Float64Array;
  sunny: Uint8Array;
  walkable: Uint8Array;
}

/** 辺コスト = 時間[min] + λ×被曝[SED] のダイクストラ */
function dijkstra(g: Grid, start: number, goal: number, lambda: number): RouteResult | null {
  const { tile, sedPerMin, walkable, sunny } = g;
  const nx = tile.cellsX;
  const n = nx * tile.cellsY;
  const dist = new Float64Array(n).fill(Infinity);
  const prev = new Int32Array(n).fill(-1);
  const done = new Uint8Array(n);
  const heap = new MinHeap();
  dist[start] = 0;
  heap.push(0, start);

  while (heap.size > 0) {
    const [c, u] = heap.pop();
    if (done[u]) continue;
    done[u] = 1;
    if (u === goal) break;
    const ux = u % nx;
    const uy = (u / nx) | 0;
    for (const [dx, dy, mul] of DIRS) {
      const vx = ux + dx;
      const vy = uy + dy;
      if (vx < 0 || vy < 0 || vx >= nx || vy >= tile.cellsY) continue;
      const v = vy * nx + vx;
      if (done[v] || !walkable[v]) continue;
      const meters = tile.cellSizeM * mul;
      const timeMin = meters / WALK_SPEED_M_PER_MIN;
      const sed = ((sedPerMin[u]! + sedPerMin[v]!) / 2) * timeMin;
      const nc = c + timeMin + lambda * sed;
      if (nc < dist[v]!) {
        dist[v] = nc;
        prev[v] = u;
        heap.push(nc, v);
      }
    }
  }
  if (prev[goal] === -1 && goal !== start) return null;

  // 経路復元＋実時間・実被曝の再集計
  const cells: number[] = [];
  for (let cur = goal; cur !== -1; cur = prev[cur]!) {
    cells.push(cur);
    if (cur === start) break;
  }
  cells.reverse();
  let timeMin = 0;
  let sed = 0;
  let shadeTime = 0;
  for (let i = 1; i < cells.length; i++) {
    const a = cells[i - 1]!;
    const b = cells[i]!;
    const ax = a % nx;
    const ay = (a / nx) | 0;
    const bx = b % nx;
    const by = (b / nx) | 0;
    const mul = ax !== bx && ay !== by ? Math.SQRT2 : 1;
    const t = (tile.cellSizeM * mul) / WALK_SPEED_M_PER_MIN;
    timeMin += t;
    sed += ((sedPerMin[a]! + sedPerMin[b]!) / 2) * t;
    if (!sunny[b]) shadeTime += t;
  }
  return {
    path: cells.map((c) => tile.cellCenter(c % nx, (c / nx) | 0)),
    sunnyFlags: cells.map((c) => sunny[c] === 1),
    timeMin,
    sed,
    shadeShare: timeMin > 0 ? shadeTime / timeMin : 0,
  };
}

/** 時間上限内で最も被曝の少ない経路（λの二分探索、16回） */
function shadeRouteWithinCap(g: Grid, start: number, goal: number, capMin: number): RouteResult | null {
  // λ上限を倍加探索：時間上限を超えるまで（=これ以上日陰に寄れない点まで）広げる
  let lo = 0;
  let hi = 1;
  let best = dijkstra(g, start, goal, 0);
  if (!best) return null;
  if (best.timeMin > capMin) return best; // 最短ですら上限超過→最短を返す（上限は満たせない）

  for (let i = 0; i < 24; i++) {
    const r = dijkstra(g, start, goal, hi);
    if (!r) break;
    if (r.timeMin > capMin) break;
    best = r;
    hi *= 4;
    if (hi > 1e9) return r; // 被曝最小経路が上限内に収まる
  }
  // (lo=上限内, hi=上限超過) を維持して二分探索
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2;
    const r = dijkstra(g, start, goal, mid);
    if (r && r.timeMin <= capMin) {
      if (r.sed <= best.sed) best = r;
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return best;
}

/** 3パターンの経路計画。開始・目的地がタイル外なら null */
export function planRoutes(q: RouteQuery): RoutePlan | null {
  const prefs: RoutePrefs = { ...DEFAULT_ROUTE_PREFS, ...q.prefs };
  const exp = buildExposure(q.tile, q.sun, q.wx);
  const g: Grid = { tile: q.tile, ...exp };

  // 街路網（最大の連結成分）へ寄せる。建物内の座標や孤立した隙間を避けるため
  const { label, largest } = labelComponents(q.tile, exp.walkable);
  if (largest < 0) return null;
  const s = snapToComponent(q.tile, label, largest, q.start);
  const t = snapToComponent(q.tile, label, largest, q.goal);
  if (!s || !t) return null;
  const start = s.y * q.tile.cellsX + s.x;
  const goal = t.y * q.tile.cellsX + t.x;

  const shortest = dijkstra(g, start, goal, 0);
  if (!shortest) return null;

  const capRatio = shortest.timeMin * prefs.maxTimeRatio;
  const capExtra = shortest.timeMin + prefs.maxExtraMinutes;
  const shadeWithinRatio = shadeRouteWithinCap(g, start, goal, capRatio) ?? shortest;
  const shadeWithinExtra = shadeRouteWithinCap(g, start, goal, capExtra) ?? shortest;

  return { shortest, shadeWithinRatio, shadeWithinExtra, prefs };
}
